import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronUp, BarChart3, PieChart, FileText, Eye, EyeOff, Copy, Download, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import Button from './Button';
import ImageThumbnail from './abtest/ImageThumbnail';
import { validateBase64Image, createSafeDataUrl, createPlaceholderDataUrl } from '../utils/imageValidation';

interface TableData {
  type: 'table';
  title: string;
  headers: string[];
  data: Array<Record<string, string | { type: 'image'; src: string; alt: string; onImageClick: () => void }>>;
}

interface CollapsibleTableProps {
  table: TableData;
  questionType?: string;
  isExporting?: boolean;
  maxInitialRows?: number;
  className?: string;
  showFullContent?: boolean; // New prop to control text truncation
  showExportButtons?: boolean; // New prop to show copy/download buttons
  pdfMode?: boolean; // For compatibility with existing TableView usage
  images?: string[]; // Array of image URLs from API response content.images
  hideTitle?: boolean; // New prop to hide the title section
  optionImages?: Record<string, string>; // Mapping of option text to image URL
  imageAttachmentLevel?: 'question' | 'option'; // Where images are attached
  onImageClick?: (src: string, alt: string) => void; // Click handler for images
  lastRowIsSummary?: boolean; // Adds visual separator before the last row (for averages/totals)
}

const CollapsibleTable: React.FC<CollapsibleTableProps> = ({ 
  table, 
  questionType = 'default', 
  isExporting = false, 
  maxInitialRows = 3,
  className = '',
  showFullContent = false, // Default to false for backward compatibility
  showExportButtons = false, // Default to false for backward compatibility
  pdfMode = false,
  images = [],
  hideTitle = false, // Default to false for backward compatibility
  optionImages,
  imageAttachmentLevel,
  onImageClick,
  lastRowIsSummary = false
}) => {
  const API_URL = import.meta.env.VITE_API_URL || '';
  const [isExpanded, setIsExpanded] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [copied, setCopied] = useState(false);
  const [cachedMarkdown, setCachedMarkdown] = useState<string | null>(null);
  const [cachedExcel, setCachedExcel] = useState<Blob | null>(null);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);

  // Force expansion when exporting, reset when export ends
  useEffect(() => {
    if (isExporting) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false); // Reset to collapsed when export ends
    }
  }, [isExporting]);

  if (!table || !table.data || table.data.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500">
        No response data available
      </div>
    );
  }

  const shouldShowCollapsible = table.data.length > maxInitialRows && !isExporting;
  // When exporting, always show all data; otherwise follow normal expansion logic
  const displayData = isExporting || isExpanded || !shouldShowCollapsible
    ? table.data
    : table.data.slice(0, maxInitialRows);

  const getQuestionTypeIcon = (type: string) => {
    switch (type) {
      case 'single_choice':
      case 'multiple_choice':
      case 'dropdown':
        return <BarChart3 className="h-4 w-4 text-blue-500" />;
      case 'matrix_rating':
      case 'matrix_grid':
        return <BarChart3 className="h-4 w-4 text-lime-500" />;
      case 'yes_no':
        return <PieChart className="h-4 w-4 text-green-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  // Determine if we should show full content for responses
  // When exporting, ALWAYS show full content regardless of local expansion state
  const shouldShowFullContent = isExporting || showFullContent || isExpanded;

  // Export functionality
  const generateMarkdown = (headers: string[], data: any[]) => {
    const headerRow = `| ${headers.join(' | ')} |`;
    const separator = `|${headers.map(() => ' --- ').join('|')}|`;
    const rows = data.map(row => {
      return `| ${headers.map(header => {
        const cell = Array.isArray(row) ? row[headers.indexOf(header)] : row[header];
        if (cell && typeof cell === 'object' && cell.type === 'image') {
          return cell.alt;
        }
        return (typeof cell === 'number' ? cell.toFixed(1) : (cell ?? ''));
      }).join(' | ')} |`;
    });
    return [headerRow, separator, ...rows].join('\n');
  };

  const generateExcel = (headers: string[], data: any[]) => {
    const wsData = [headers, ...data.map(row => headers.map(header => {
      const cell = Array.isArray(row) ? row[headers.indexOf(header)] : row[header];
      if (cell && typeof cell === 'object' && cell.type === 'image') {
        return cell.alt;
      }
      return cell;
    }))];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    return new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  };

  useEffect(() => {
    if (showExportButtons) {
      setCachedMarkdown(generateMarkdown(table.headers, table.data));
      setCachedExcel(generateExcel(table.headers, table.data));
    }
  }, [table.headers, table.data, showExportButtons]);

  const handleCopy = async () => {
    if (!cachedMarkdown) return;
    try {
      await navigator.clipboard.writeText(cachedMarkdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      // fallback: do nothing
    }
  };

  const handleDownload = () => {
    if (!cachedExcel) return;
    const url = URL.createObjectURL(cachedExcel);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${table.title || 'table'}.xlsx`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Helper function to render cell content with image support
  const renderCellContent = (cell: any, header: string) => {
    // Handle multiple choice "selected choices" column with images
    if ((header === "selected choices" || header === "selected_choices") && imageAttachmentLevel === 'option' && optionImages && typeof cell === 'string') {
      // Split comma-separated selections
      const selections = cell.split(',').map(s => s.trim());
      const imageElements = selections.map(selection => {
        // Try exact match first
        let imageUrl = optionImages[selection];
        let displayText = selection;
        
        // If no exact match, check if selection is a number and try "Option {number}" format
        if (!imageUrl) {
          const numericMatch = selection.match(/^\d+$/);
          if (numericMatch) {
            const optionKey = `Option ${selection}`;
            imageUrl = optionImages[optionKey];
            if (imageUrl) {
              displayText = optionKey;
            }
          }
        }
        
        if (imageUrl) {
          const fullImageUrl = imageUrl.startsWith('http') ? imageUrl : `${API_URL}${imageUrl}`;
          return { url: fullImageUrl, label: displayText };
        }
        return null;
      }).filter((img): img is { url: string; label: string } => img !== null);
      
      if (imageElements.length > 0) {
        // When exporting, use inline styles for reliable PDF rendering
        if (isExporting) {
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {imageElements.map((img, idx) => (
                <img
                  key={idx}
                  src={img.url}
                  alt={img.label}
                  style={{ 
                    height: '48px', 
                    width: '48px', 
                    objectFit: 'cover', 
                    borderRadius: '4px',
                    border: '1px solid #d1d5db'
                  }}
                />
              ))}
            </div>
          );
        }
        // Regular interactive display
        return (
          <div className="flex flex-wrap gap-2">
            {imageElements.map((img, idx) => (
              <div key={idx} className="flex items-center">
                <img
                  src={img.url}
                  alt={img.label}
                  className="h-12 w-12 object-cover rounded border border-gray-300 cursor-pointer hover:border-blue-400 transition-colors"
                  onClick={() => onImageClick?.(img.url, img.label)}
                />
              </div>
            ))}
          </div>
        );
      }
    }
    
    // Check if this cell is a single option with an attached image (only for 'choice' column)
    if ((header === "choice" || header === "Choice") && imageAttachmentLevel === 'option' && optionImages && typeof cell === 'string') {
      // Try exact match first
      let imageUrl = optionImages[cell];
      let displayText = cell;
      
      // If no exact match, check if cell is a number and try "Option {number}" format
      if (!imageUrl) {
        const numericMatch = cell.match(/^\d+$/);
        if (numericMatch) {
          const optionKey = `Option ${cell}`;
          imageUrl = optionImages[optionKey];
          if (imageUrl) {
            displayText = optionKey; // Show "Option 1" instead of just "1"
          }
        }
      }
      
      if (imageUrl) {
        // This option has an image - show only the image
        const fullImageUrl = imageUrl.startsWith('http') ? imageUrl : `${API_URL}${imageUrl}`;
        
        // When exporting, use inline styles for reliable PDF rendering
        if (isExporting) {
          return (
            <img
              src={fullImageUrl}
              alt={displayText}
              style={{ 
                height: '48px', 
                width: '48px', 
                objectFit: 'cover', 
                borderRadius: '4px',
                border: '1px solid #d1d5db'
              }}
            />
          );
        }
        // Regular interactive display
        return (
          <div className="flex items-center">
            <img
              src={fullImageUrl}
              alt={displayText}
              className="h-12 w-12 object-cover rounded border border-gray-300 cursor-pointer hover:border-blue-400 transition-colors"
              onClick={() => onImageClick?.(fullImageUrl, displayText)}
            />
          </div>
        );
      }
    }
    
    // Check if this is already an image object
    if (cell && typeof cell === 'object' && cell.type === 'image') {
      // When exporting, use simple img tag instead of ImageThumbnail
      if (isExporting) {
        let imageSrc = cell.src.startsWith('data:') ? cell.src : `data:image/jpeg;base64,${cell.src}`;
        
        // Validate and create safe data URL
        try {
          imageSrc = createSafeDataUrl(cell.src, 'jpeg');
          
          // Additional validation for the final URL
          if (!validateBase64Image(imageSrc)) {
            console.warn('Invalid image data in table cell, using placeholder');
            imageSrc = createPlaceholderDataUrl();
          }
        } catch (error) {
          console.warn('Error processing image in table cell:', error);
          imageSrc = createPlaceholderDataUrl();
        }
        
        return (
          <img 
            src={imageSrc} 
            alt={cell.alt}
            className="w-12 h-12 object-cover rounded border border-gray-200"
            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
            onError={(e) => {
              console.warn('Image failed to load in PDF export, using placeholder');
              (e.target as HTMLImageElement).src = createPlaceholderDataUrl();
            }}
          />
        );
      }
      return (
        <ImageThumbnail
          src={cell.src}
          alt={cell.alt}
          onClick={cell.onImageClick}
        />
      );
    }

    // Check if header is "ad_index" or "Ad Index" and we have images
    if ((header.toLowerCase() === 'ad_index' || header === 'Ad Index') && images.length > 0) {
      const index = parseInt(cell);
      if (!isNaN(index) && index >= 0 && index < images.length) {
        // Handle base64 image data with validation
        let imageSrc: string;
        
        try {
          imageSrc = createSafeDataUrl(images[index], 'jpeg');
          
          // Additional validation
          if (!validateBase64Image(imageSrc)) {
            console.warn(`Invalid image data for ad_index ${index}, using placeholder`);
            imageSrc = createPlaceholderDataUrl();
          }
        } catch (error) {
          console.warn(`Error processing ad_index image ${index}:`, error);
          imageSrc = createPlaceholderDataUrl();
        }
        
        // When exporting, use simple img tag instead of ImageThumbnail
        if (isExporting) {
          return (
            <img 
              src={imageSrc} 
              alt={`Ad ${index + 1}`}
              className="w-12 h-12 object-cover rounded border border-gray-200"
              style={{ width: '48px', height: '48px', objectFit: 'cover' }}
              onError={(e) => {
                console.warn(`Ad image ${index} failed to load in PDF export, using placeholder`);
                (e.target as HTMLImageElement).src = createPlaceholderDataUrl();
              }}
            />
          );
        }
        
        return (
          <ImageThumbnail
            src={imageSrc}
            alt={`Ad ${index + 1}`}
            onClick={() => setExpandedImage(imageSrc)}
          />
        );
      }
    }

    // Check if cell value is like "image 1", "image 2", etc.
    if (typeof cell === 'string') {
      const imageMatch = cell.toLowerCase().match(/^image\s+(\d+)$/);
      if (imageMatch && images.length > 0) {
        const imageIndex = parseInt(imageMatch[1]) - 1; // Convert to 0-based index

        if (imageIndex >= 0 && imageIndex < images.length) {
          // Handle base64 image data with validation
          let imageSrc: string;
          
          try {
            imageSrc = createSafeDataUrl(images[imageIndex], 'jpeg');
            
            // Additional validation
            if (!validateBase64Image(imageSrc)) {
              console.warn(`Invalid image data for "${cell}", using placeholder`);
              imageSrc = createPlaceholderDataUrl();
            }
          } catch (error) {
            console.warn(`Error processing image "${cell}":`, error);
            imageSrc = createPlaceholderDataUrl();
          }
          
          // When exporting, use simple img tag instead of ImageThumbnail
          if (isExporting) {
            return (
              <img 
                src={imageSrc} 
                alt={cell}
                onError={(e) => {
                  console.warn(`Image "${cell}" failed to load in PDF export, using placeholder`);
                  (e.target as HTMLImageElement).src = createPlaceholderDataUrl();
                }}
                className="w-12 h-12 object-cover rounded border border-gray-200"
                style={{ width: '48px', height: '48px', objectFit: 'cover' }}
              />
            );
          }
          
          return (
            <ImageThumbnail
              src={imageSrc}
              alt={cell}
              onClick={() => setExpandedImage(imageSrc)}
            />
          );
        }
      }
    }

    // Default text rendering
    return (
      <div className={shouldShowFullContent ? '' : 'max-w-xs'}>
        {typeof cell === 'string' && cell.length > 100 && !shouldShowFullContent
          ? `${cell.substring(0, 100)}...`
          : typeof cell === 'number' ? cell.toFixed(1) : cell || '-'
        }
      </div>
    );
  };

  return (
    <div className={`bg-white rounded-lg border border-gray-200 overflow-hidden ${className}`}>
      {!hideTitle ? (
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <h4 className="text-sm font-medium text-gray-900">{table.title}</h4>
              <span className="text-xs text-gray-500">
                ({table.data.length} responses)
              </span>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Export buttons */}
              {showExportButtons && (
                <div className="flex gap-2 no-print">
                  <div className="relative">
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<Download className="h-4 w-4" />}
                      onClick={handleDownload}
                      title="Download table as Excel"
                      onMouseEnter={() => setShowTooltip('download')}
                      onMouseLeave={() => setShowTooltip(null)}
                    >
                      {''}
                    </Button>
                    {showTooltip === 'download' && (
                      <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                        Download table as Excel
                      </div>
                    )}
                  </div>
                  <div className="relative">
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<Copy className="h-4 w-4" />}
                      onClick={handleCopy}
                      title="Copy table as Markdown"
                      onMouseEnter={() => setShowTooltip('copy')}
                      onMouseLeave={() => setShowTooltip(null)}
                    >
                      {''}
                    </Button>
                    {showTooltip === 'copy' && (
                      <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                        Copy table as Markdown
                      </div>
                    )}
                  </div>
                  {copied && (
                    <span className="text-green-600 text-xs ml-1">Copied!</span>
                  )}
                </div>
              )}
              
              {/* Hide/Show Preview button */}
              {shouldShowCollapsible && (
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="flex items-center space-x-1 text-xs text-gray-600 hover:text-gray-800 transition-colors"
                >
                  {showPreview ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  <span>{showPreview ? 'Hide' : 'Show'} Preview</span>
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        // When title is hidden, still show export buttons if enabled
        showExportButtons && (
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <div className="flex justify-end">
              <div className="flex gap-2 no-print">
                <div className="relative">
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Download className="h-4 w-4" />}
                    onClick={handleDownload}
                    title="Download table as Excel"
                    onMouseEnter={() => setShowTooltip('download')}
                    onMouseLeave={() => setShowTooltip(null)}
                  >
                    {''}
                  </Button>
                  {showTooltip === 'download' && (
                    <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                      Download table as Excel
                    </div>
                  )}
                </div>
                <div className="relative">
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Copy className="h-4 w-4" />}
                    onClick={handleCopy}
                    title="Copy table as Markdown"
                    onMouseEnter={() => setShowTooltip('copy')}
                    onMouseLeave={() => setShowTooltip(null)}
                  >
                    {''}
                  </Button>
                  {showTooltip === 'copy' && (
                    <div className="absolute right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded z-20 whitespace-nowrap">
                      Copy table as Markdown
                    </div>
                  )}
                </div>
                {copied && (
                  <span className="text-green-600 text-xs ml-1">Copied!</span>
                )}
              </div>
            </div>
          </div>
        )
      )}

      {showPreview && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            {pdfMode ? (
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Render header as first row for PDF mode */}
                <tr>
                  {table.headers.map((header, i) => (
                    <th
                      key={i}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {header.replace(/_/g, ' ')}
                    </th>
                  ))}
                </tr>
                {displayData.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {table.headers.map((header, cellIndex) => {
                      const cell = Array.isArray(row) ? row[cellIndex] : row[header];
                      return (
                        <td
                          key={cellIndex}
                          className="px-6 py-4 text-sm text-gray-500 break-words max-w-[300px]"
                        >
                          {pdfMode && cell && typeof cell === 'object' && cell.type === 'image' ? (
                            <span>{cell.alt}</span>
                          ) : (
                            renderCellContent(cell, header)
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            ) : (
              <>
                <thead className="bg-gray-50">
                  <tr>
                    {table.headers.map((header, index) => (
                      <th
                        key={index}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-700 tracking-wide"
                      >
                        {header.replace(/_/g, ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {displayData.map((row, rowIndex) => {
                    const isLastRow = rowIndex === displayData.length - 1;
                    const isSummaryRow = lastRowIsSummary && isLastRow;
                    return (
                      <tr 
                        key={rowIndex} 
                        className={`
                          ${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}
                          ${isSummaryRow ? '!border-t-[3px] !border-t-gray-400 bg-gray-100 font-semibold' : ''}
                        `}
                      >
                        {table.headers.map((header, cellIndex) => {
                          const cell = Array.isArray(row) ? row[cellIndex] : row[header];
                          return (
                            <td key={cellIndex} className="px-4 py-3 text-sm text-gray-700 leading-relaxed">
                              {renderCellContent(cell, header)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </>
            )}
          </table>

          {shouldShowCollapsible && (
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center space-x-2 text-sm text-[#07E5D1] hover:text-[#06D4C2] transition-colors font-medium"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    <span>Show Less ({maxInitialRows} of {table.data.length})</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    <span>Show All ({table.data.length - maxInitialRows} more responses)</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Image Modal - Rendered via Portal to avoid z-index issues */}
      {expandedImage && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setExpandedImage(null)}
        >
          <div className="relative bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden max-w-4xl max-h-[90vh] w-full flex flex-col">
            {/* Close button */}
            <button
              onClick={() => setExpandedImage(null)}
              className="absolute top-4 right-4 z-10 bg-white hover:bg-gray-100 text-gray-700 hover:text-gray-900 rounded-full p-2 shadow-lg transition-colors border border-gray-200"
            >
              <X className="h-5 w-5" />
            </button>
            
            {/* Image content */}
            <div className="flex-1 flex items-center justify-center p-6">
              <img 
                src={expandedImage} 
                alt="Expanded view" 
                className="max-w-full max-h-full object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CollapsibleTable;